/* @jsx h */
import { defineComponent, h, ref, computed, onMounted, Ref } from "vue";
import { api } from "@app/data/index";
import {
  Order,
  PaymentDetails,
  SuccesfullPartialPayment,
  TableCheckout,
} from "@lemmonhq/types";
import { formatPrice, round } from "@lemmonhq/orders";
import { Loading } from "@app/vue/components/index";
import { useLocalization } from "@lemmonhq/uikit";
import {
  calculateRemainingTotal,
  calculatePaidTotal,
  createPartialPaymentPayload,
  createSplitPaymentPayload,
  getRemainingUnpaidItemQuantities,
} from "@app/vue/pages/Tables/payments/paymentUtils";
import { calculateOrderTotal } from "@lemmonhq/orders";
import { PartialPaymentContent } from "@app/vue/pages/Tables/payments/PartialPaymentContent";
import { SplitPaymentContent } from "@app/vue/pages/Tables/payments/SplitPaymentContent";
import { calculatePartialTotal } from "@app/utils/payments";
import { useAuth } from "@app/vue/providers/Auth";

export type PaymentEditorMode = "partial" | "split";

export type TableCheckoutEditorProps = {
  tableCheckout: TableCheckout;
  onChange: (tableCheckout: TableCheckout) => void;
};

export const TableCheckoutEditor = defineComponent<TableCheckoutEditorProps>({
  name: "TableCheckoutEditor",
  props: ["tableCheckout", "onChange"],
  setup(props) {
    const auth = useAuth();
    const { t } = useLocalization();
    const loading = ref(true);
    const orders = ref<Order[]>([]);
    const partialPayments = ref<SuccesfullPartialPayment[]>([]);
    const donePayments = ref<PaymentDetails[]>([]);
    const expandedGroups = ref<Set<string>>(new Set());
    const partialSelectedItems = ref<Record<number, number>>({});
    const splitAmount = ref(0);
    const splitCustomAmount = ref(0);
    const splitCount = ref<number>(2);
    const activeSplitTab = ref("equal");

    const mode = ref<PaymentEditorMode>(
      props.tableCheckout.type === "split" ? "split" : "partial",
    );

    const tableId = props.tableCheckout.table_id;

    const fetchData = async () => {
      try {
        loading.value = true;
        const tablesMeta = await api.getTablesMeta();
        const tableMeta = tablesMeta.find((el) => el.id === tableId);
        if (!tableMeta) throw new Error("Table not found");

        const orderIds = tableMeta.orders.map((o) => o.id);
        if (orderIds.length === 0) {
          orders.value = [];
          return;
        }

        const [ordersData, paymentsData] = await Promise.all([
          api.getOrders(orderIds),
          api.getTablePayments(orderIds),
        ]);

        orders.value = ordersData;
        const currentPartialPayments = paymentsData.partial_payments || [];
        partialPayments.value = currentPartialPayments;
        donePayments.value = (paymentsData.payments || []).filter(
          (el) => el.status === "success" || el.status === "authorized",
        );

        // Pre-populate selected items if current checkout is partial
        if (props.tableCheckout.type === "partial") {
          const currentItems = props.tableCheckout.items;
          const selection: Record<number, number> = {};
          currentItems.forEach((item) => {
            selection[item.id] = item.quantity;
          });
          partialSelectedItems.value = selection;
        } else if (props.tableCheckout.type === "full") {
          partialSelectedItems.value = getRemainingUnpaidItemQuantities(
            ordersData,
            currentPartialPayments,
          );
        }
      } finally {
        loading.value = false;
      }
    };

    onMounted(fetchData);

    const subTotal = computed(() =>
      orders.value.reduce((sum, order) => sum + calculateOrderTotal(order), 0),
    );
    const paidTotal = computed(() => calculatePaidTotal(donePayments.value));
    const remainingTotal = computed(() =>
      calculateRemainingTotal(orders.value, donePayments.value),
    );
    const remainingUnpaidItems = computed(() =>
      getRemainingUnpaidItemQuantities(orders.value, partialPayments.value),
    );
    const selectedItemsForCheckout = computed(() =>
      Object.keys(partialSelectedItems.value).length > 0
        ? partialSelectedItems.value
        : remainingUnpaidItems.value,
    );

    const nextPaymentTotal = computed(() => {
      if (mode.value === "partial") {
        return Object.keys(selectedItemsForCheckout.value).length === 0
          ? remainingTotal.value
          : calculatePartialTotal(
              orders.value.map((el) => el.items || []).flat(),
              selectedItemsForCheckout.value,
            );
      }
      if (mode.value === "split") {
        return splitAmount.value;
      }
      return remainingTotal.value;
    });

    const buildPayload = (): TableCheckout | null => {
      if (mode.value === "partial") {
        if (Object.keys(selectedItemsForCheckout.value).length === 0)
          return null;
        return createPartialPaymentPayload(
          orders.value,
          selectedItemsForCheckout.value,
          tableId,
          partialPayments.value,
        );
      }
      if (mode.value === "split" && splitAmount.value > 0) {
        return createSplitPaymentPayload(
          orders.value,
          partialPayments.value,
          tableId,
          splitAmount.value,
          donePayments.value.filter((el) => el.split_payment_id).length,
          activeSplitTab.value,
        );
      }
      return null;
    };

    const handleApply = () => {
      const payload = buildPayload();
      if (!payload) return;
      props.onChange(payload);
    };

    const canApply = computed(() => {
      if (mode.value === "partial")
        return Object.keys(selectedItemsForCheckout.value).length > 0;
      if (mode.value === "split")
        return Number.isFinite(splitAmount.value) && splitAmount.value > 0;
      return false;
    });

    return () => {
      if (loading.value) {
        return (
          <div class="flex justify-center py-16">
            <Loading />
          </div>
        );
      }

      return (
        <div class="flex flex-1 flex-col gap-4">
          {/* Mode switcher */}
          <div class="flex gap-2">
            <button
              class={`btn btn-sm flex-1 ${mode.value === "partial" ? "btn-primary" : "btn-neutral"}`}
              disabled={!!donePayments.value.find((el) => el.split_payment_id)}
              onClick={() => {
                mode.value = "partial";
              }}
            >
              <span class="material-symbols-outlined text-base">receipt</span>
              {t("tables.checkout.partial") || "Partial"}
            </button>
            <button
              class={`btn btn-sm flex-1 ${mode.value === "split" ? "btn-primary" : "btn-neutral"}`}
              disabled={
                !!donePayments.value.find((el) => el.partial_payment_id)
              }
              onClick={() => {
                mode.value = "split";
              }}
            >
              <span class="material-symbols-outlined text-base">
                arrow_split
              </span>
              {t("tables.checkout.split_button") || "Split"}
            </button>
          </div>

          {/* Items / split content */}
          <div class="min-h-[200px] flex-1 overflow-y-auto">
            {mode.value !== "split" && (
              <PartialPaymentContent
                orders={orders.value}
                partialPayments={partialPayments.value}
                selectedItems={partialSelectedItems.value}
                onSelectItem={(id: number, qty: number) => {
                  if (mode.value !== "partial") return;
                  if (qty === 0) {
                    const { [id]: _, ...rest } = partialSelectedItems.value;
                    partialSelectedItems.value = rest;
                  } else {
                    partialSelectedItems.value = {
                      ...partialSelectedItems.value,
                      [id]: qty,
                    };
                  }
                }}
                expandedGroups={expandedGroups}
              />
            )}
            {mode.value === "split" && (
              <SplitPaymentContent
                remainingTotal={remainingTotal.value}
                donePayments={donePayments.value}
                splitAmount={splitAmount}
                splitCustomAmount={splitCustomAmount}
                splitCount={splitCount}
                subtotal={subTotal}
                activeSplitTab={activeSplitTab}
              />
            )}
          </div>

          {/* Totals */}
          <div class="border-t border-base-content/10 pt-3 space-y-1 text-sm">
            <div class="flex justify-between text-base-content/60">
              <span>{t("tables.checkout.subtotal") || "Subtotal"}</span>
              <span>
                {formatPrice(
                  subTotal.value,
                  true,
                  2,
                  auth.user.value?.restaurant.currency || "CHF",
                )}
              </span>
            </div>
            {paidTotal.value > 0 && (
              <div class="flex justify-between text-base-content/60">
                <span>{t("tables.checkout.paid") || "Paid"}</span>
                <span>
                  -{" "}
                  {formatPrice(
                    paidTotal.value,
                    true,
                    2,
                    auth.user.value?.restaurant.currency || "CHF",
                  )}
                </span>
              </div>
            )}
            <div class="flex justify-between font-semibold text-accent">
              <span>
                {mode.value === "split"
                  ? t("tables.checkout.split_amount") || "Split amount"
                  : Object.keys(selectedItemsForCheckout.value).length > 0
                    ? "Selected"
                    : t("tables.checkout.remaining") || "Remaining"}
              </span>
              <span>
                {formatPrice(
                  nextPaymentTotal.value,
                  true,
                  2,
                  auth.user.value?.restaurant.currency || "CHF",
                )}
              </span>
            </div>
          </div>

          {/* Apply button */}
          <button
            class="btn btn-primary w-full"
            disabled={!canApply.value}
            onClick={handleApply}
          >
            <span class="material-symbols-outlined">check_circle</span>
            Apply changes
          </button>
        </div>
      );
    };
  },
});
